import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import { TOOLS } from "@/config/tools";
import { StatusBadge, KpiTile, currency } from "@/components/Primitives";
import { ArrowUpRight, BedDouble, Bath, Ruler, Home as HomeIcon, CalendarDays, Megaphone, FileSignature, TrendingUp, Users } from "lucide-react";

const STAGE = { coming_soon: ["Coming Soon", "amber"], for_sale: ["For Sale", "green"], under_contract: ["Under Contract", "orange"], sold: ["Sold", "emerald"] };
const abs = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "TBD");
const daysToClose = (s) => {
  if (!s) return null;
  const d = new Date(s + "T00:00:00");
  if (isNaN(d)) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86400000);
};
const closeBadge = (n) => {
  if (n == null) return null;
  const label = n < 0 ? `${Math.abs(n)}d overdue` : n === 0 ? "Today" : n === 1 ? "1 day" : `${n} days`;
  const tone = n < 0 ? "bg-[#F6E4E4] text-[#992B2B]" : n <= 7 ? "bg-[#FDECEC] text-[#C0392B]" : n <= 30 ? "bg-[#FDF0E1] text-[#B4690E]" : "bg-[#EAF0FB] text-[#1F3C88]";
  return { label, tone };
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [properties, setProperties] = useState({ listings: [], contracts: [], counts: { listings: 0, contracts: 0 } });
  const navigate = useNavigate();

  const visibleTools = TOOLS.filter((t) => user.tools.includes(t.key) && t.key !== "users");
  const canUsers = user.tools.includes("users");
  const canTxn = user.tools.includes("transactions");

  useEffect(() => {
    const load = async () => {
      const out = {};
      const jobs = [];
      jobs.push(api.get("/dashboard/company-stats").then((r) => (out.company = r.data)).catch(() => {}));
      if (canUsers) jobs.push(api.get("/admin/users").then((r) => (out.users = r.data.length)).catch(() => {}));
      jobs.push(api.get("/properties").then((r) => setProperties(r.data)).catch(() => {}));
      await Promise.all(jobs);
      setStats(out);
    };
    load();
  }, [user, canUsers]);

  const firstName = (user.name || "there").split(" ")[0];
  const headshot = user.headshot ? (user.headshot.startsWith("http") ? user.headshot : `${process.env.REACT_APP_BACKEND_URL}${user.headshot}`) : "";

  return (
    <div data-testid="dashboard-page">
      <div className="mb-10 animate-fade-up flex items-center gap-5">
        {headshot ? <img src={headshot} alt="" data-testid="dashboard-avatar" className="w-16 h-16 rounded-full object-cover shrink-0 border border-[#E5E5E2]" /> : null}
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#F7941D]">Command Center</div>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl tracking-tight leading-none text-[#0A0A0A]">
            Welcome back, {firstName}.
          </h1>
          <p className="mt-3 text-[#575755] max-w-2xl">
            Jump into any workspace below.
          </p>
        </div>
      </div>

      {/* Key stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {stats.company && <KpiTile index={0} testid="kpi-volume-ytd" color="#1F3C88" Icon={TrendingUp} value={stats.company.volume_ytd} label="Volume YTD" rows={[["Closed & pending this year", ""]]} />}
        {stats.company && <KpiTile index={1} testid="kpi-active-listings" color="#16A34A" Icon={Megaphone} value={stats.company.active_listings} label="Active Listings" rows={[["Company-wide", ""]]} />}
        {stats.company && <KpiTile index={2} testid="kpi-pending-contracts" color="#F59E0B" Icon={FileSignature} value={stats.company.pending_contracts} label="Pending Contracts" rows={[["Company-wide", ""]]} />}
        {canUsers && stats.users != null && <KpiTile index={3} testid="kpi-team" color="#6D4AFF" Icon={Users} value={stats.users} label="Team Members" rows={[["Across all roles", ""]]} />}
      </div>

      {/* Company properties */}
      <div className="flex items-center gap-2 mb-4">
        <Megaphone className="w-4 h-4 text-[#1F3C88]" />
        <h2 className="font-serif text-2xl tracking-tight text-[#0A0A0A]">Active Listings</h2>
        <span className="text-sm text-[#8a8a86]">{properties.counts.listings}</span>
      </div>
      {properties.listings.length === 0 && <p className="text-sm text-[#8a8a86] mb-10">No active listings.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-12">
        {properties.listings.map((p) => {
          const [stageLabel, stageTone] = STAGE[p.stage] || STAGE.for_sale;
          return (
            <button key={p.id} data-testid={`dash-listing-${p.id}`}
              onClick={() => {
                if (p.assigned) { navigate(`/listings/${p.id}`); }
                else if (p.slug) { window.open(`/p/${p.slug}`, "_blank", "noopener"); }
                else { toast.info("No public preview available for this listing yet."); }
              }}
              title={p.assigned ? "Open listing details" : "View landing page preview"}
              className="group text-left bg-white border border-[#E5E5E2] rounded-sm overflow-hidden hover:border-[#F7941D] transition-colors duration-200">
              <div className="relative h-28 bg-[#F0EFEC]">
                {p.image ? <img src={abs(p.image)} alt={p.address} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><HomeIcon className="w-6 h-6 text-[#C9C9C4]" /></div>}
                <div className="absolute top-2 left-2"><StatusBadge tone={stageTone}>{stageLabel}</StatusBadge></div>
                {!p.assigned && <div className="absolute top-2 right-2"><StatusBadge tone="gray">Preview</StatusBadge></div>}
              </div>
              <div className="p-3">
                <div className="font-serif text-lg text-[#1F3C88] leading-tight">{currency(p.price)}</div>
                <div className="text-xs text-[#575755] truncate">{p.address || "Untitled"}</div>
                <div className="text-[11px] text-[#8a8a86] truncate">{p.city}</div>
                <div className="mt-2 flex items-center gap-2 text-[11px] text-[#575755]">
                  <span className="flex items-center gap-0.5"><BedDouble className="w-3 h-3" />{p.beds ?? "—"}</span>
                  <span className="flex items-center gap-0.5"><Bath className="w-3 h-3" />{p.baths ?? "—"}</span>
                  <span className="flex items-center gap-0.5"><Ruler className="w-3 h-3" />{p.sqft?.toLocaleString() ?? "—"}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <FileSignature className="w-4 h-4 text-[#1F3C88]" />
        <h2 className="font-serif text-2xl tracking-tight text-[#0A0A0A]">Active Contracts</h2>
        <span className="text-sm text-[#8a8a86]">{properties.counts.contracts}</span>
      </div>
      {properties.contracts.length === 0 && <p className="text-sm text-[#8a8a86] mb-12">No active contracts.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-14">
        {[...properties.contracts].sort((a, b) => (a.closing_date || "9999-12-31").localeCompare(b.closing_date || "9999-12-31")).map((c) => (
          <button key={c.id} data-testid={`dash-contract-${c.id}`} onClick={() => canTxn && navigate(`/transactions/${c.id}`)}
            className={`group text-left bg-white border border-[#E5E5E2] rounded-sm overflow-hidden transition-colors duration-200 ${canTxn ? "hover:border-[#F7941D] cursor-pointer" : "cursor-default"}`}>
            {c.image ? (
              <div className="h-28 w-full overflow-hidden bg-[#F0EFEC]">
                <img src={c.image} alt={c.address} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </div>
            ) : (
              <div className="h-28 w-full bg-[#EAF0FB] flex items-center justify-center"><HomeIcon className="w-8 h-8 text-[#C3D0EC]" strokeWidth={1.5} /></div>
            )}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="font-serif text-lg text-[#1F3C88] leading-tight">{currency(c.price)}</div>
                <StatusBadge tone={c.side === "seller" ? "amber" : "gray"}>{(c.side || "buyer") === "seller" ? "Listing" : "Buyer"}</StatusBadge>
              </div>
              <div className="text-xs text-[#575755] truncate mt-1">{c.address}</div>
              {(c.beds || c.baths || c.sqft) && (
                <div className="mt-2 flex items-center gap-2 text-[11px] text-[#575755]">
                  <span className="flex items-center gap-0.5"><BedDouble className="w-3 h-3" />{c.beds ?? "—"}</span>
                  <span className="flex items-center gap-0.5"><Bath className="w-3 h-3" />{c.baths ?? "—"}</span>
                  <span className="flex items-center gap-0.5"><Ruler className="w-3 h-3" />{c.sqft?.toLocaleString() ?? "—"}</span>
                </div>
              )}
              <div className="mt-3 pt-2 border-t border-[#F0EFEC] flex items-center justify-between gap-1.5 text-[11px] text-[#8a8a86]">
                <span className="flex items-center gap-1.5"><CalendarDays className="w-3 h-3" /> Closing {fmtDate(c.closing_date)}</span>
                {closeBadge(daysToClose(c.closing_date)) && (
                  <span data-testid={`dash-contract-countdown-${c.id}`} className={`shrink-0 px-1.5 py-0.5 rounded-full font-semibold ${closeBadge(daysToClose(c.closing_date)).tone}`}>{closeBadge(daysToClose(c.closing_date)).label}</span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Tool cards */}
      <h2 className="font-serif text-2xl tracking-tight text-[#0A0A0A] mb-5">Your Workspaces</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...visibleTools, ...(canUsers ? TOOLS.filter((t) => t.key === "users") : [])].map((tool, i) => {
          const Icon = tool.icon;
          return (
            <Link
              key={tool.key}
              to={tool.path}
              data-testid={`tool-card-${tool.key}`}
              className="group bg-white border border-[#E5E5E2] rounded-sm p-7 hover:border-[#F7941D] transition-colors duration-200 animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-sm bg-[#1F3C88] flex items-center justify-center group-hover:bg-[#F7941D] transition-colors duration-200">
                  <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <ArrowUpRight className="w-5 h-5 text-[#B9B5AC] group-hover:text-[#F7941D] transition-colors duration-200" />
              </div>
              <div className="mt-5 font-serif text-2xl text-[#0A0A0A]">{tool.name}</div>
              <div className="mt-2 text-sm text-[#575755]">{tool.desc}</div>
            </Link>
          );
        })}
      </div>

      {/* External tools */}
      <h2 className="font-serif text-2xl tracking-tight text-[#0A0A0A] mt-12 mb-5">Your Tools</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { key: "followupboss", name: "Follow Up Boss", desc: "CRM & lead follow-up", url: "https://app.followupboss.com", logo: "/tool-logos/followupboss.png" },
          { key: "realscout", name: "RealScout", desc: "Home search & buyer alerts", url: "https://www.realscout.com/agents/sign_in", logo: "/tool-logos/realscout.png" },
          { key: "dotloop", name: "Dotloop", desc: "Transactions & eSignature", url: "https://www.dotloop.com", logo: "/tool-logos/dotloop.png" },
          { key: "cincymls", name: "Cincinnati MLS", desc: "MLS of Greater Cincinnati", url: "https://portal.cincyrealtoralliance.com/", logo: "/tool-logos/cincymls.png" },
          { key: "daytonmls", name: "Dayton MLS", desc: "Dayton REALTORS MLS", url: "https://iam.dabr.com/idp/login", logo: "/tool-logos/daytonmls.jpg" },
          { key: "titletoolbox", name: "Title Toolbox", desc: "Property data & farming (Sterling Land Title)", url: "https://www.profarmohio.com/SterlingLandTitle/#!/user/dashboard", logo: "/tool-logos/titletoolbox.svg" },
          { key: "showingtime", name: "ShowingTime", desc: "Showings & appointment scheduling", url: "https://showingti.me/Account/Login?ReturnUrl=%2f", logo: "/tool-logos/showingtime.jpeg" },
          { key: "cma", name: "CMA Tool", desc: "Team AI market analysis (Gemini)", url: "https://gemini.google.com/gem/1AW9jfwE2VGMU7LC7ftdz-7i5jMtgKBSc?usp=sharing", logo: "/tool-logos/cma.png" },
          { key: "newlisting", name: "New Listing Input Form", desc: "Submit a new listing to the team", url: "https://docs.google.com/forms/d/e/1FAIpQLScX-xR2_5pHCQiN0QgAa_oM8puxdVm1v6sP2nNxqRJvghMFFw/viewform", logo: "/tool-logos/new-listing-form.png" },
        ].map((tool, i) => (
          <a
            key={tool.key}
            href={tool.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`external-tool-${tool.key}`}
            className="group bg-white border border-[#E5E5E2] rounded-sm p-7 hover:border-[#F7941D] transition-colors duration-200 animate-fade-up flex items-center gap-5"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="w-16 h-16 rounded-sm bg-[#FBFAF8] border border-[#F0EFEC] flex items-center justify-center shrink-0 overflow-hidden p-2">
              <img src={tool.logo} alt={`${tool.name} logo`} className="max-w-full max-h-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-2xl text-[#0A0A0A] flex items-center gap-1.5">
                {tool.name}
                <ArrowUpRight className="w-4 h-4 text-[#B9B5AC] group-hover:text-[#F7941D] transition-colors duration-200" />
              </div>
              <div className="mt-1 text-sm text-[#575755]">{tool.desc}</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
